import { FC, useEffect, useState } from "react";

import {
  Column,
  CommunicationPreferenceFormType,
  getFilenameFromUrl,
  getOppositeLocale,
  TicketingFormCategory,
} from "@simplyk/common";
import { useSnackbar } from "notistack";
import { FormProvider, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { useLiveFormAmplitudeEvents } from "../../../../common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "../../../../common/LiveFormEditorContext";
import { swapTranslationContent } from "../helpers/swapTranslationContent";
import { ReminderTranslationDefaults } from "../helpers/reminderTranslations";
import { buildTicketingEmailTranslationText } from "../helpers/emailTranslationText";
import { TranslationSwapVersionContext } from "../sharedComponents/TranslationSwapContext";
import { TranslationModalLayout } from "../TranslationModalLayout/TranslationModalLayout";
import { TicketingTranslationData, TicketingTranslationsData } from "../translation.types";

import { TicketingTranslationColumn } from "./TicketingTranslationColumn";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { AutoHideDuration } from "@/common/constants/Notistack";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { mapDataToEditChoiceFields, mapDataToEditQuestionFields } from "@/common/helpers/customQuestion";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingOutput } from "@/common/types/trpc";
import { FormEmailContents } from "@/features/LiveFormEditor/common/formEmailContents";
import { useCommunicationPreferencesForPreview } from "@/features/LiveFormEditor/LivePreview/useCommunicationPreferencesForPreview";

interface TicketingTranslationFormProps {
  closeModal: () => void;
  onSave: () => void;
  reminders: ReminderTranslationDefaults[];
  emailContents: FormEmailContents;
}

export const TicketingTranslationForm: FC<TicketingTranslationFormProps> = ({
  closeModal,
  onSave,
  reminders,
  emailContents,
}) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { apiFormObject: apiTicketing, updateApiFormObject } = useLiveFormEditorContext<"ticketing">();
  const { organization } = useCurrentUserContext();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const [isTranslating, setIsTranslating] = useState(false);
  const [swapVersion, setSwapVersion] = useState(0);

  const { mutateAsync: updateTicketingTranslations } = trpc.updateTicketingTranslations.useMutation();
  const { questionTitle: communicationPreferencesTitle } = useCommunicationPreferencesForPreview(
    apiTicketing.id,
    CommunicationPreferenceFormType.Ticketing
  );
  const utils = trpc.useUtils();
  const formLocale = apiTicketing.locale;
  const translationLocale = getOppositeLocale({ organizationCountry: organization?.country, locale: formLocale });

  const defaultTicketingFields = apiTicketing?.ticketingFields?.find((field) => field.locale === apiTicketing?.locale);
  const defaultTicketingTranslations = apiTicketing?.ticketingFields?.find(
    (field) => field.locale === translationLocale
  );
  const isAuction = apiTicketing.formCategory === TicketingFormCategory.Auction;
  const { emailAttachmentUrl: fieldsAttachmentUrl, ...fieldsEmailText } = buildTicketingEmailTranslationText(
    emailContents,
    formLocale,
    isAuction
  );
  const { emailAttachmentUrl: translationsAttachmentUrl, ...translationsEmailText } =
    buildTicketingEmailTranslationText(emailContents, translationLocale, isAuction);

  const defaultValues = {
    fields: {
      id: defaultTicketingFields?.id,
      title: defaultTicketingFields?.title || "",
      description: defaultTicketingFields?.description || "",
      ...fieldsEmailText,
      chequeDescription: defaultTicketingFields?.chequeDescription || undefined,
      offlinePaymentWording: defaultTicketingFields?.offlinePaymentWording || undefined,
      attachment: {
        fileUrl: fieldsAttachmentUrl,
        filename: getFilenameFromUrl(fieldsAttachmentUrl),
      },
      reminders: Object.fromEntries(reminders.map((reminder) => [reminder.key, reminder.fields])),
    },
    translations: {
      id: defaultTicketingTranslations?.id || v4(),
      title: defaultTicketingTranslations?.title || "",
      description: defaultTicketingTranslations?.description || "",
      ...translationsEmailText,
      chequeDescription: defaultTicketingTranslations?.chequeDescription || undefined,
      offlinePaymentWording: defaultTicketingTranslations?.offlinePaymentWording || undefined,
      attachment: {
        fileUrl: translationsAttachmentUrl,
        filename: getFilenameFromUrl(translationsAttachmentUrl),
      },
      reminders: Object.fromEntries(reminders.map((reminder) => [reminder.key, reminder.translations])),
    },
  };

  const form = useForm<TicketingTranslationsData>({
    defaultValues,
    mode: "onChange",
    shouldUnregister: true,
  });

  useEffect(() => {
    if (Object.values(form.formState.errors).length > 0) {
      enqueueSnackbar(t("dashboard", "donationForm.alertMissingTranslations"), { vibe: "danger" });
    }
  }, [enqueueSnackbar, form.formState.errors, t]);

  const requestEditionForm = async () => {
    const submitFormData = form.getValues();
    const { attachment: _1, ...fields } = mirrorPaymentInstructions(
      submitFormData.fields,
      defaultValues.fields,
      isAuction
    );
    const { attachment: _2, ...translations } = mirrorPaymentInstructions(
      submitFormData.translations,
      defaultValues.translations,
      isAuction
    );

    const ticketingTranslations = {
      fields: {
        ...fields,
        questionsFields: mapDataToEditQuestionFields(submitFormData.fields.questionsFields || [], apiTicketing.locale),
        choiceFields: mapDataToEditChoiceFields(
          submitFormData.fields.questionsFields?.flatMap((questionField) => questionField.choices || []) || [],
          apiTicketing.questions?.flatMap((question) => question.choices!) || [],
          apiTicketing.locale
        ),
        emailAttachmentUrl: submitFormData.fields.attachment?.fileUrl,
      },
      translations: {
        ...translations,
        questionsFields: mapDataToEditQuestionFields(
          submitFormData.translations.questionsFields || [],
          translationLocale
        ),
        choiceFields: mapDataToEditChoiceFields(
          submitFormData.translations.questionsFields?.flatMap((questionField) => questionField.choices || []) || [],
          apiTicketing.questions?.flatMap((question) => question.choices!) || [],
          translationLocale
        ),
        emailAttachmentUrl: submitFormData.translations.attachment?.fileUrl,
        communicationPreferencesTitle: submitFormData.communicationPreferencesTitle,
      },
    };

    return await updateTicketingTranslations({
      ticketingId: apiTicketing.id,
      ticketingTranslations,
    });
  };

  const handleSwapTranslations = () => {
    const { fields, translations } = form.getValues();
    const [swappedFields, swappedTranslations] = swapTranslationContent(fields, translations);

    form.setValue(Column.Fields, swappedFields, { shouldDirty: true });
    form.setValue(Column.Translations, swappedTranslations, { shouldDirty: true });
    setSwapVersion((version) => version + 1);
  };

  const onSubmit = form.handleSubmit(async () => {
    if (!apiTicketing) {
      return;
    }

    const { data, error } = await requestEditionForm();

    if (error) {
      enqueueSnackbar(t("dashboard", "donationForm.edit.translations.error"), {
        vibe: "danger",
        autoHideDuration: AutoHideDuration.fast,
      });
    }

    if (data) {
      enqueueSnackbar(t("dashboard", "donationForm.edit.translations"), {
        vibe: "positive",
        autoHideDuration: AutoHideDuration.fast,
      });

      updateApiFormObject(data as Partial<TicketingOutput>);
      await Promise.all([
        utils.getLiveFormRemindersData.invalidate({ ticketingId: apiTicketing.id }),
        utils.getAutomatedEmailContents.invalidate({ ticketingId: apiTicketing.id }),
      ]);
      logUserActionEvent(AmplitudeEvents.FormTranslationAdded);
      onSave();
      closeModal();
    }
  });

  return (
    <FormProvider {...form}>
      <TranslationModalLayout
        open
        onClose={closeModal}
        onSubmit={onSubmit}
        onSwap={handleSwapTranslations}
        formLocale={formLocale}
        translationLocale={translationLocale}
        organizationCountry={organization?.country}
        isTranslating={isTranslating}
        isSaving={form.formState.isSubmitting}
        slots={{
          content: (
            <TranslationSwapVersionContext.Provider value={swapVersion}>
              <TicketingTranslationColumn
                data={apiTicketing}
                formLocale={formLocale}
                translationLocale={translationLocale}
                isAuction={isAuction}
                reminders={reminders}
                emailText={{ fields: fieldsEmailText, translations: translationsEmailText }}
                communicationPreferencesTitle={communicationPreferencesTitle}
                onTranslationStateChange={setIsTranslating}
              />
            </TranslationSwapVersionContext.Provider>
          ),
        }}
      />
    </FormProvider>
  );
};

const mirrorPaymentInstructions = (
  values: TicketingTranslationData,
  stored: Pick<TicketingTranslationData, "chequeDescription" | "offlinePaymentWording">,
  isAuction: boolean
): TicketingTranslationData =>
  isAuction
    ? { ...values, chequeDescription: values.offlinePaymentWording ?? stored.chequeDescription }
    : { ...values, offlinePaymentWording: values.chequeDescription ?? stored.offlinePaymentWording };
